/**
 * aeterna/neural/charcnn.ts — Character-Level Convolutional Affect Classifier
 *
 * No tokenization needed. Slide banks of 1D conv kernels over raw UTF-8
 * character bytes → max-pool → classify into affect dimensions.
 *
 * Three kernel sizes (3, 5, 7) × 32 filters each capture character n-gram
 * patterns: trigrams catch common emotional words, pentagrams catch phrases,
 * heptagrams catch longer patterns like "I don't understand" or "thank you."
 *
 * The whole thing is ~30KB of weights and runs in <1ms.
 *
 * Specifically useful for detecting emotional tone in player chat before
 * the EntityZero API call — lets the Aeterna pre-configure its response
 * temperature locally without a round trip.
 *
 * Weights are initialized from a deterministic seed. In production,
 * these would be loaded from a trained checkpoint. The architecture
 * is ready for both online learning and pre-trained deployment.
 */

import {
  zeros, conv1D, maxPool1D, dense, vtanh, vsigmoid,
  randomMatrix, mulberry32, gaussianSeeded,
  matvec, vadd,
} from "./math.js";
import { scoreLexicon, AFFECT_DIM } from "./affect-lexicon.js";
import { tokenize } from "./embeddings.js";
import { affectMLP } from "./affect-mlp.js";

// ─── Config ──────────────────────────────────────────────────────────────────

/** Maximum input length in characters */
const MAX_LEN = 256;

/** Character embedding dimension (raw byte = 256 possible values, embed to this) */
const CHAR_EMBED_DIM = 32;

/** Number of filters per kernel size */
const NUM_FILTERS = 32;

/** Kernel sizes to use */
const KERNEL_SIZES = [3, 5, 7] as const;

/** Total pooled features: NUM_FILTERS × len(KERNEL_SIZES) */
const POOL_DIM = NUM_FILTERS * KERNEL_SIZES.length; // 96

/** Hidden layer dimension */
const HIDDEN_DIM = 48;

/** Output dimension (affect vector) */
const OUTPUT_DIM = 8;

// ─── Affect labels (same as ESN) ─────────────────────────────────────────────

export const AFFECT_LABELS = [
  "warmth", "tension", "curiosity", "melancholy",
  "playfulness", "depth", "urgency", "openness",
] as const;

export interface CharAffect {
  warmth: number;
  tension: number;
  curiosity: number;
  melancholy: number;
  playfulness: number;
  depth: number;
  urgency: number;
  openness: number;
  /** Overall emotional intensity (norm of the affect vector) */
  intensity: number;
}

// ─── CharCNN ─────────────────────────────────────────────────────────────────

export class CharCNN {
  // Character embedding matrix: 256 → CHAR_EMBED_DIM
  private charEmbed: Float32Array;

  // Convolution kernels and biases per kernel size
  private kernels: Float32Array[];
  private biases: Float32Array[];

  // FC layers
  private W_hidden: Float32Array;
  private b_hidden: Float32Array;
  private W_out: Float32Array;
  private b_out: Float32Array;

  constructor(seed = 2024) {
    const rng = mulberry32(seed);

    // Character embedding: 256 chars × CHAR_EMBED_DIM
    this.charEmbed = new Float32Array(256 * CHAR_EMBED_DIM);
    const embedScale = 1 / Math.sqrt(CHAR_EMBED_DIM);
    for (let i = 0; i < this.charEmbed.length; i++) {
      this.charEmbed[i] = gaussianSeeded(rng) * embedScale;
    }

    // Conv kernels
    this.kernels = [];
    this.biases = [];
    for (const kw of KERNEL_SIZES) {
      // Kernel shape: NUM_FILTERS × kw × CHAR_EMBED_DIM (stored flat)
      const k = new Float32Array(NUM_FILTERS * kw * CHAR_EMBED_DIM);
      const kScale = 1 / Math.sqrt(kw * CHAR_EMBED_DIM);
      for (let i = 0; i < k.length; i++) k[i] = gaussianSeeded(rng) * kScale;
      this.kernels.push(k);
      this.biases.push(zeros(NUM_FILTERS));
    }

    // Hidden FC: POOL_DIM → HIDDEN_DIM
    this.W_hidden = randomMatrix(HIDDEN_DIM, POOL_DIM, 1 / Math.sqrt(POOL_DIM), seed + 100);
    this.b_hidden = zeros(HIDDEN_DIM);

    // Output FC: HIDDEN_DIM → OUTPUT_DIM
    this.W_out = randomMatrix(OUTPUT_DIM, HIDDEN_DIM, 1 / Math.sqrt(HIDDEN_DIM), seed + 200);
    this.b_out = zeros(OUTPUT_DIM);

    // Initialize output biases to center (0.5 after sigmoid)
    // (sigmoid(0) = 0.5)

    // ── Structural character embedding bias ─────────────────────────────
    // Without this, the CharCNN outputs ~0.5 for everything until enough
    // data flows through to train the ESN readout. The Aeterna is
    // emotionally blind for the first hour.
    //
    // Fix: give emotionally relevant characters distinctive embedding
    // signatures so conv filters can detect them immediately.
    this.seedCharacterBiases();
    this.seedOutputBiases();
  }

  /**
   * Structural biases for character embeddings.
   * Ensures punctuation, capitalization, and emotionally-relevant
   * characters have distinctive representations from init.
   */
  private seedCharacterBiases(): void {
    const bias = (charCode: number, dim: number, value: number) => {
      this.charEmbed[charCode * CHAR_EMBED_DIM + (dim % CHAR_EMBED_DIM)] += value;
    };

    // Exclamation marks → urgency + playfulness dimensions
    bias(33, 0, 0.8);  bias(33, 4, 0.6);  bias(33, 6, 0.7);
    // Question marks → curiosity + openness
    bias(63, 2, 0.9);  bias(63, 7, 0.7);
    // Period → depth (definitive statements)
    bias(46, 5, 0.3);
    // Ellipsis components: period gets melancholy bump
    bias(46, 3, 0.2);
    // Comma → openness (sentence continues)
    bias(44, 7, 0.2);

    // Uppercase letters: A-Z (65-90) → tension + urgency
    for (let c = 65; c <= 90; c++) {
      bias(c, 1, 0.3);  // tension
      bias(c, 6, 0.2);  // urgency
    }

    // Lowercase letters: a-z (97-122) → baseline warmth
    for (let c = 97; c <= 122; c++) {
      bias(c, 0, 0.05);  // slight warmth
    }

    // Space (32) → neutral (already is)
    // Heart-like characters: < > 3 (ASCII approximation of <3)
    bias(60, 0, 0.3);  // <
    bias(51, 0, 0.5);  // 3 (as in <3)
    bias(62, 0, 0.3);  // >

    // Parentheses → playfulness (emoticons)
    bias(40, 4, 0.3);   // (
    bias(41, 4, 0.3);   // )
    // Colon → playfulness (emoticons like :) :( :D)
    bias(58, 4, 0.4);

    // Tilde (~) → playfulness
    bias(126, 4, 0.5);

    // Dash (-) → melancholy/depth
    bias(45, 3, 0.2);  bias(45, 5, 0.2);

    // Numbers (48-57) → low affect (factual)
    for (let c = 48; c <= 57; c++) {
      bias(c, 5, 0.15);  // slight depth (statistics, specifics)
    }
  }

  /**
   * Bias output layer so that character-pattern signals
   * propagate to meaningful affect dimensions even with
   * random hidden weights. This is a weak prior, not hard-coding.
   */
  private seedOutputBiases(): void {
    // Shift output biases slightly from 0 to create asymmetry:
    // warmth slightly positive, tension slightly negative
    // This means unknown text reads as "slightly warm, slightly open"
    // rather than dead-center neutral on every dimension.
    this.b_out[0] =  0.15;  // warmth: assume good faith
    this.b_out[1] = -0.10;  // tension: assume calm unless proven otherwise
    this.b_out[2] =  0.05;  // curiosity: slight baseline
    this.b_out[3] = -0.05;  // melancholy: assume not sad
    this.b_out[4] =  0.10;  // playfulness: slight baseline
    this.b_out[5] =  0.00;  // depth: neutral
    this.b_out[6] = -0.05;  // urgency: assume not urgent
    this.b_out[7] =  0.10;  // openness: assume open
  }

  /**
   * Classify a text string into an affect vector.
   * Returns 8-dim affect + intensity score.
   *
   * Priority: pre-trained MLP (if loaded) → conv pipeline + lexicon blend.
   * The MLP is a distilled GoEmotions classifier (~270KB int8 binary).
   * When available, it provides dramatically better affect grounding.
   * Lexicon is still blended in for word-level ground truth either way.
   */
  classify(text: string): CharAffect {
    // Fast path: pre-trained MLP provides the neural signal
    if (affectMLP.isLoaded()) {
      const mlpResult = affectMLP.classify(text);
      if (mlpResult) {
        // Still blend lexicon for known-word grounding
        const tokens = tokenize(text);
        const lexResult = scoreLexicon(tokens);
        const lexWeight = Math.min(0.4, lexResult.coverage * 1.0); // lower weight: MLP is trusted
        const mlpWeight = 1 - lexWeight;
        const blended: CharAffect = {
          warmth:       mlpResult.warmth * mlpWeight + lexResult.affect[0] * lexWeight,
          tension:      mlpResult.tension * mlpWeight + lexResult.affect[1] * lexWeight,
          curiosity:    mlpResult.curiosity * mlpWeight + lexResult.affect[2] * lexWeight,
          melancholy:   mlpResult.melancholy * mlpWeight + lexResult.affect[3] * lexWeight,
          playfulness:  mlpResult.playfulness * mlpWeight + lexResult.affect[4] * lexWeight,
          depth:        mlpResult.depth * mlpWeight + lexResult.affect[5] * lexWeight,
          urgency:      mlpResult.urgency * mlpWeight + lexResult.affect[6] * lexWeight,
          openness:     mlpResult.openness * mlpWeight + lexResult.affect[7] * lexWeight,
          intensity:    mlpResult.intensity, // MLP intensity is authoritative
        };
        return blended;
      }
    }

    // Fallback: conv pipeline + lexicon blend (original implementation)
    // Encode text to bytes
    const bytes = new TextEncoder().encode(text.slice(0, MAX_LEN));
    const seqLen = bytes.length;

    if (seqLen === 0) {
      return {
        warmth: 0.5, tension: 0.5, curiosity: 0.5, melancholy: 0.5,
        playfulness: 0.5, depth: 0.5, urgency: 0.5, openness: 0.5,
        intensity: 0,
      };
    }

    // Character embedding lookup: bytes → [seqLen × CHAR_EMBED_DIM]
    const embedded = new Float32Array(seqLen * CHAR_EMBED_DIM);
    for (let t = 0; t < seqLen; t++) {
      const byteVal = bytes[t];
      const srcOff = byteVal * CHAR_EMBED_DIM;
      const dstOff = t * CHAR_EMBED_DIM;
      for (let d = 0; d < CHAR_EMBED_DIM; d++) {
        embedded[dstOff + d] = this.charEmbed[srcOff + d];
      }
    }

    // Convolution + max-pool for each kernel size
    const pooled = zeros(POOL_DIM);
    let poolOffset = 0;

    for (let ki = 0; ki < KERNEL_SIZES.length; ki++) {
      const kw = KERNEL_SIZES[ki];
      if (seqLen < kw) {
        // Sequence too short for this kernel — output zeros
        poolOffset += NUM_FILTERS;
        continue;
      }

      // Conv1D: [seqLen × CHAR_EMBED_DIM] * kernel → [(seqLen-kw+1) × NUM_FILTERS]
      const convOut = conv1D(
        embedded, seqLen, CHAR_EMBED_DIM,
        this.kernels[ki], kw, NUM_FILTERS,
        this.biases[ki],
      );

      // ReLU activation
      for (let i = 0; i < convOut.length; i++) {
        if (convOut[i] < 0) convOut[i] = 0;
      }

      // Max-pool over time → [NUM_FILTERS]
      const outLen = seqLen - kw + 1;
      const pooledK = maxPool1D(convOut, NUM_FILTERS, outLen);

      // Copy to pooled features
      for (let f = 0; f < NUM_FILTERS; f++) {
        pooled[poolOffset + f] = pooledK[f];
      }
      poolOffset += NUM_FILTERS;
    }

    // FC hidden layer with tanh
    const hidden = dense(pooled, this.W_hidden, HIDDEN_DIM, POOL_DIM, this.b_hidden, vtanh);

    // FC output layer with sigmoid (affect values in [0, 1])
    const preOutput = matvec(this.W_out, OUTPUT_DIM, HIDDEN_DIM, hidden);
    vadd(preOutput, this.b_out, preOutput);
    const output = vsigmoid(preOutput);

    // ── Lexicon grounding: blend ground-truth affect for known words ──────
    // The conv pipeline captures character-level patterns (punctuation,
    // capitalization, rhythm). The lexicon provides word-level ground truth.
    // For messages with high lexicon coverage, the lexicon dominates.
    // For messages with no matches, the conv output passes through unchanged.
    const tokens = tokenize(text);
    const lexResult = scoreLexicon(tokens);
    const finalOutput = new Float32Array(OUTPUT_DIM);

    // Blend weight: 0 at 0% coverage, 0.7 at 50%+ coverage (saturates)
    const lexWeight = Math.min(0.7, lexResult.coverage * 1.4);
    const convWeight = 1 - lexWeight;

    for (let i = 0; i < OUTPUT_DIM; i++) {
      finalOutput[i] = output[i] * convWeight + lexResult.affect[i] * lexWeight;
    }

    // Compute intensity as deviation from neutral (0.5)
    let intensitySum = 0;
    for (let i = 0; i < OUTPUT_DIM; i++) {
      const dev = finalOutput[i] - 0.5;
      intensitySum += dev * dev;
    }
    const intensity = Math.sqrt(intensitySum / OUTPUT_DIM) * 2; // normalize to ~[0, 1]

    return {
      warmth:       finalOutput[0],
      tension:      finalOutput[1],
      curiosity:    finalOutput[2],
      melancholy:   finalOutput[3],
      playfulness:  finalOutput[4],
      depth:        finalOutput[5],
      urgency:      finalOutput[6],
      openness:     finalOutput[7],
      intensity,
    };
  }

  /**
   * Get raw affect vector as Float32Array (for Hopfield storage, ESN input, etc.)
   */
  classifyRaw(text: string): Float32Array {
    const affect = this.classify(text);
    return new Float32Array([
      affect.warmth, affect.tension, affect.curiosity, affect.melancholy,
      affect.playfulness, affect.depth, affect.urgency, affect.openness,
    ]);
  }

  // ─── Weight Import/Export ──────────────────────────────────────────────────

  exportWeights(): {
    charEmbed: number[];
    kernels: number[][];
    biases: number[][];
    W_hidden: number[];
    b_hidden: number[];
    W_out: number[];
    b_out: number[];
  } {
    return {
      charEmbed: Array.from(this.charEmbed),
      kernels: this.kernels.map(k => Array.from(k)),
      biases: this.biases.map(b => Array.from(b)),
      W_hidden: Array.from(this.W_hidden),
      b_hidden: Array.from(this.b_hidden),
      W_out: Array.from(this.W_out),
      b_out: Array.from(this.b_out),
    };
  }

  importWeights(data: ReturnType<CharCNN["exportWeights"]>): void {
    this.charEmbed = new Float32Array(data.charEmbed);
    for (let i = 0; i < this.kernels.length; i++) {
      this.kernels[i] = new Float32Array(data.kernels[i]);
      this.biases[i] = new Float32Array(data.biases[i]);
    }
    this.W_hidden = new Float32Array(data.W_hidden);
    this.b_hidden = new Float32Array(data.b_hidden);
    this.W_out = new Float32Array(data.W_out);
    this.b_out = new Float32Array(data.b_out);
  }
}
